import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { RequestId } from '@shared/brand';
import { seedColorForSubArea, subAreaColor } from '@domain/entities/Organization';
import type { VacationRequest } from '@domain/entities/VacationRequest';
import { CONFIRMED_ABSENCE_STATUSES, type RequestStatus } from '@domain/vocabulary';
import { DayOfWeek } from '@domain/value-objects/CalendarDate';
import { DateRange } from '@domain/value-objects/DateRange';
import { getServices } from '@infrastructure/services';
import { groupRescheduleChains } from '../Requests/rescheduleChains';
import { useAccess } from '../../hooks/useAccess';
import { useApprovalScope } from '../../hooks/useApprovalScope';
import { useOwnAreaName } from '../../hooks/useAreaName';
import { expiringLive, newestLive, periodYearFor } from '../../balancePeriod';
import { useLiveBalances } from '../../hooks/useLiveBalances';
import { useSession } from '../../providers/SessionProvider';
import { lifecycleSteps, type LifecycleStep } from '../../lifecycle';
import { chooseAbsenceTip } from './absenceTip';
import { useAreaHomeSections, type AreaHomeSections } from './useAreaHomeSections';
import { useSystemHomeSections, type SystemHomeSections } from './useSystemHomeSections';
import {
  formatDateRange,
  formatDayWithYear,
  incapacityStatusColors,
  permissionStatusColors,
  statusAccentColor,
  statusChipColors,
} from '../../format';

/**
 * The Inicio view-model.
 *
 * All the composition lives here so `HomeScreen` stays a pure rendering
 * function. What the screen shows depends on the role, so the queries are
 * conditional: an employee never fetches the area's approval queue, and a
 * superuser — who holds neither `solicitar` nor `perfil` — fetches nothing
 * personal at all.
 *
 * Every read is bounded and server-side counted. The stat cells use `count()`
 * rather than downloading documents to measure them; at 500 people that is the
 * difference between four reads and several thousand.
 */

export interface StatCell {
  readonly label: string;
  readonly value: number | string;
  readonly chip: string;
  readonly note: string;
  readonly color: string;
}

export interface RecentRequest {
  readonly id: RequestId;
  readonly month: string;
  readonly day: number;
  readonly rangeLabel: string;
  readonly meta: string;
  readonly status: RequestStatus;
  readonly statusBackground: string;
  readonly statusForeground: string;
  readonly statusAccent: string;
  readonly steps: readonly LifecycleStep[];
}

/**
 * A row in the leader's inbox.
 *
 * Vacations, medical leaves and permits land in one list because a leader's
 * question is "what needs me today", not "what needs me in the vacation module".
 * The kind chip and the action label carry the difference.
 */
export interface QueueRow {
  readonly id: string;
  /** Free text: the permit kinds come from the runtime-editable catalogue. */
  readonly kind: string;
  /**
   * Which board this row is decided on.
   *
   * Carried per row because «Aprobaciones» holds three of them now, and the row's
   * own button used to land on whichever one happened to be first — a permit sent
   * the leader to the vacation kanban, where the permit was not.
   */
  readonly board: 'vacaciones' | 'permisos' | 'incapacidades';
  readonly kindBackground: string;
  readonly kindForeground: string;
  /** The left rail follows the absence type, not the approval status. */
  readonly kindAccent: string;
  readonly folio: string;
  readonly personName: string;
  readonly meta: string;
  readonly status: string;
  readonly statusBackground: string;
  readonly statusForeground: string;
  /** Sub-area colour, painted as the row's 4px left rail. */
  readonly accentColor: string;
  /** Contextual, because "Aprobar" is wrong for a second signature. */
  readonly actionLabel: string;
}

export interface OutRow {
  readonly id: string;
  readonly name: string;
  readonly subAreaName: string;
  /** A quién buscar mientras no está. Vacío si la ausencia no lleva respaldo. */
  readonly replacementName: string;
  readonly rangeLabel: string;
  readonly color: string;
}

export interface ExpiryBanner {
  readonly days: number;
  readonly headline: string;
  readonly note: string;
}

/**
 * Which home this is.
 *
 * The four roles do not share a home screen with switches on it — they are four
 * different screens that happen to share a header. Naming the variant lets the
 * view branch once at the top rather than testing permissions at every panel.
 */
export type HomeVariant = 'employee' | 'leader' | 'admin' | 'super';

export interface HomeViewModel {
  readonly variant: HomeVariant;
  readonly greeting: string;
  readonly subtitle: string;
  readonly statsTitle: string;
  readonly statsScope: string;
  readonly stats: readonly StatCell[];
  readonly expiry: ExpiryBanner | null;
  readonly myRequests: readonly RecentRequest[];
  readonly pendingApprovals: readonly QueueRow[];
  readonly outThisWeek: readonly OutRow[];
  readonly tip: string | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly onNewRequest: () => void;
  readonly onScheduleExpiring: () => void;
  readonly onOpenRequests: () => void;
  readonly onOpenCalendar: () => void;
  readonly onOpenBoard: (board?: QueueRow['board']) => void;
  readonly onOpenAdmin: () => void;
  readonly onOpenConfig: () => void;
  readonly onOpenLog: () => void;
  /** Populated only for `admin`. */
  readonly area: AreaHomeSections;
  /** Populated only for `super`. */
  readonly system: SystemHomeSections;
}

const RECENT_LIMIT = 3;
const QUEUE_LIMIT = 6;
const OUT_LIMIT = 5;

const MONTHS_SHORT = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
] as const;

export const useHomeViewModel = (): HomeViewModel => {
  const session = useSession();
  const { holds } = useAccess();
  /*
   * Inicio no sigue al selector, y por eso pide **su** área por nombre.
   *
   * La mitad de esta pantalla es personal —su saldo, sus días por vencer— y esa
   * no puede cambiar de dueño porque alguien mire otra área. Con el nombre
   * enfocado y los datos propios, el encabezado decía «Académico» sobre cifras de
   * Tecnología: una etiqueta que miente es peor que no tener etiqueta. Lo global
   * se mira en «Panorama de la empresa».
   */
  const areaName = useOwnAreaName();
  const approval = useApprovalScope();
  const navigate = useNavigate();

  const canRequest = holds('solicitar');
  const canSeeOwn = holds('perfil') || canRequest;
  const canApprove = holds('aprobar');
  const canSeeArea = holds('calendario_area');
  const today = getServices().clock.today();
  const year = today.year;

  /*
   * The variant is decided from the role rather than from permissions, because
   * the home is the one screen whose *shape* follows the role. Permissions still
   * gate every control inside it; a superuser granted `aprobar` through the
   * matrix gets the approve buttons, but not an employee's balance card.
   */
  const variant: HomeVariant =
    session.role === 'super'
      ? 'super'
      : session.role === 'admin'
        ? 'admin'
        : canApprove
          ? 'leader'
          : 'employee';

  const area = useAreaHomeSections(variant === 'admin');
  const system = useSystemHomeSections(variant === 'super');

  /** Own recent requests. Only fetched for a role that files them. */
  const myRequests = useQuery({
    queryKey: ['home', 'myRequests', session.personId],
    enabled: canSeeOwn,
    queryFn: async () => {
      const result = await getServices().requests.list(
        { personId: session.personId },
        { limit: RECENT_LIMIT },
      );
      if (!result.ok) throw new Error(describe(result.error));
      /*
       * The same collapse «Mis solicitudes» applies: a reschedule leaves the
       * request it replaced behind, and listing both here would show the same
       * holiday twice in a panel that only has room for three rows — the older
       * attempts would push out unrelated requests the person actually needs to
       * see. Only the live end of each chain counts as recent.
       */
      return groupRescheduleChains(result.value.items).map((chain) => chain.head);
    },
  });

  /** Own balances, one per live period, for the expiring-days band and the stat cells. */
  const balance = useLiveBalances({
    scope: 'home',
    personId: session.personId,
    hireDate: session.hireDate,
    expiryMonths: session.config.rules.expiryMonths,
    enabled: canSeeOwn,
  });

  /**
   * The approval queue. Only fetched for a role that can decide, and scoped to
   * what that role may decide on — a leader's own sub area, an administrator's
   * whole area — with the leader's own requests excluded so the inbox never
   * offers them a decision on themselves.
   */
  const queue = useQuery({
    queryKey: ['home', 'queue', approval.scope, session.personId],
    enabled: canApprove && !approval.loading,
    queryFn: async () => {
      const result = await getServices().requests.list(
        { ...approval.scope, statuses: ['Por revisar', 'En revisión', 'Visto bueno'] },
        { limit: QUEUE_LIMIT },
      );
      if (!result.ok) throw new Error(describe(result.error));
      return approval.mine(result.value.items);
    },
  });

  /** Medical leaves and permits awaiting the same leader. */
  const pendingIncapacities = useQuery({
    queryKey: ['home', 'queueIncapacities', approval.scope, session.personId],
    enabled: canApprove && !approval.loading,
    queryFn: async () => {
      const result = await getServices().incapacities.list(
        { ...approval.scope, statuses: ['Por validar', 'Observada'] },
        { limit: QUEUE_LIMIT },
      );
      if (!result.ok) return [];
      return approval.mine(result.value.items);
    },
  });

  const pendingPermits = useQuery({
    queryKey: ['home', 'queuePermits', approval.scope, session.personId],
    enabled: canApprove && !approval.loading,
    queryFn: async () => {
      const result = await getServices().permissions.list(
        { ...approval.scope, statuses: ['Por aprobar'] },
        { limit: QUEUE_LIMIT },
      );
      if (!result.ok) return [];
      return approval.mine(result.value.items);
    },
  });

  /**
   * «Esta semana» es lo que **queda** de ella: de hoy al viernes.
   *
   * Empezó siendo `hoy−7 … hoy+7` —quince días bajo un rótulo que dice «esta
   * semana»—, pasó a ser lunes-a-domingo, y acabó aquí por lo mismo que la
   * sugerencia: esta sección se lee para decidir fechas, y los días que ya
   * pasaron no se pueden esquivar. Un miércoles, contar a quien estuvo fuera el
   * lunes es exacto y no ayuda a nadie.
   *
   * Termina el viernes porque el viernes termina la semana de trabajo, y
   * `onOrNext` es lo que hace que eso siga significando algo en fin de semana: un
   * sábado apunta al viernes que viene, no al que acaba de pasar, así que la
   * ventana nunca se invierte ni pasa de una semana.
   *
   * Las dos frases que la nombran —el panel «Fuera esta semana» y el «N
   * compañeros fuera esta semana» del saludo— **vienen del prototipo** y se
   * quedan como están: lo que cambió es qué se mide, no cómo se llama.
   */
  const week = useMemo(() => {
    const range = DateRange.create(today, today.onOrNext(DayOfWeek.Friday));
    return range.ok ? range.value : null;
  }, [today]);

  const outNow = useQuery({
    // Keyed on the person too: the result excludes their own absences, so an
    // area-only key would serve one colleague's list to another.
    queryKey: ['home', 'outNow', session.areaId, session.personId, week?.toString() ?? ''],
    enabled: canSeeArea && week !== null,
    queryFn: async () => {
      if (week === null) return [];
      const result = await getServices().requests.list(
        // Decididas, no solicitadas: esta lista afirma quién está fuera, y una
        // solicitud sin firmar todavía no lo dice de nadie.
        { areaId: session.areaId, statuses: CONFIRMED_ABSENCE_STATUSES, touching: week },
        { limit: 50 },
      );
      if (!result.ok) throw new Error(describe(result.error));
      return result.value.items.filter((r) => r.personId !== session.personId);
    },
  });

  /**
   * Cuánta gente está fuera hoy.
   *
   * Dos cosas que costaron una ronda de QA y conviene no deshacer. La primera son
   * los estados: contaba `OCCUPYING_STATUSES`, así que quien apenas había pedido
   * el viernes ya figuraba como ausente. La segunda vive en el repositorio — el
   * contador no aplicaba la mitad en memoria del solape, así que sumaba toda
   * ausencia que terminara hoy **o después**, vacaciones de septiembre incluidas.
   * Juntas dieron un 7 el día que había dos personas fuera.
   */
  const outCount = useQuery({
    queryKey: ['home', 'outCount', session.areaId, today.toISO()],
    enabled: canApprove || canSeeArea,
    queryFn: async () => {
      const todayRange = DateRange.create(today, today);
      if (!todayRange.ok) return 0;
      const result = await getServices().requests.count({
        areaId: session.areaId,
        statuses: CONFIRMED_ABSENCE_STATUSES,
        touching: todayRange.value,
      });
      if (!result.ok) throw new Error(describe(result.error));
      return result.value;
    },
  });

  const activePeople = useQuery({
    queryKey: ['home', 'activePeople', session.areaId],
    enabled: canApprove || canSeeArea,
    queryFn: async () => {
      const result = await getServices().people.list(
        { areaId: session.areaId, status: 'active' },
        { limit: 300 },
      );
      if (!result.ok) return 0;
      return result.value.items.filter(
        (person) => person.role !== 'admin' && person.role !== 'super',
      ).length;
    },
  });

  const subAreas = useQuery({
    queryKey: ['home', 'subAreas', session.areaId],
    enabled: canSeeArea,
    queryFn: async () => {
      const result = await getServices().organization.listSubAreas(session.areaId);
      return result.ok ? result.value : [];
    },
  });

  const go = useCallback(
    (path: string) => () => {
      void navigate(path);
    },
    [navigate],
  );

  // A person with no balance document yet is not an error — it is their first
  // days at the company. An empty list lets the UI omit the cells rather than
  // showing a failure.
  const lives = useMemo(() => balance.data ?? [], [balance.data]);
  const currentLive = newestLive(lives);
  const currentBalance = currentLive?.balance ?? null;
  const mine = useMemo(() => myRequests.data ?? [], [myRequests.data]);

  const stats = useMemo<StatCell[]>(() => {
    const items: StatCell[] = [];

    /*
     * A superuser's figures are about the platform, not a period. They also stop
     * before «Fuera hoy» — that number belongs to an area, and a superuser has
     * none, so showing it would attribute one area's absences to the company.
     */
    if (variant === 'super') {
      return [
        {
          label: 'Features activas',
          value: system.activeFeatureCount,
          chip: 'Global',
          note: `de ${String(system.activeFeatureCount + system.offFeatureCount)} disponibles`,
          color: 'var(--color-violet)',
        },
        {
          label: 'Áreas',
          value: system.areaCount,
          chip: 'Org',
          note: `${String(system.areas.filter((a) => a.active).length)} activas`,
          color: 'var(--color-brand)',
        },
        {
          label: 'Sub áreas',
          value: system.subAreaCount,
          chip: 'Org',
          note: 'en toda la empresa',
          color: 'var(--color-success)',
        },
        {
          label: 'Módulos apagados',
          value: system.offFeatureCount,
          chip: 'Off',
          note: 'ocultos en todos los perfiles',
          color: 'var(--color-warning)',
        },
      ];
    }

    /* An administrator does not file requests, so a balance would read as zero. */
    if (variant === 'admin') {
      items.push({
        label: 'Sub áreas',
        value: area.subAreaCount,
        chip: 'Área',
        note: `configuradas en ${areaName}`,
        color: 'var(--color-brand)',
      });
      items.push({
        label: 'Cuentas por validar',
        value: area.pendingAccounts,
        chip: 'Admin',
        note: 'registros nuevos',
        color: 'var(--color-warning)',
      });
      items.push({
        label: 'Feriados activos',
        value: area.activeHolidays,
        chip: 'LFT',
        note: `calendario oficial ${String(year)}`,
        color: 'var(--color-violet)',
      });
    } else if (canSeeOwn && currentLive !== null && currentBalance !== null) {
      /*
       * Una celda por periodo vigente, del que vence antes al que vence después.
       *
       * Con «Vencimiento del saldo» en más de 12 meses lo que queda del periodo
       * anterior sigue valiendo junto al nuevo, y al pedir se gasta primero. Una
       * sola cifra sumada escondería justo lo que importa: cuáles vencen y cuándo.
       */
      for (const live of lives) {
        items.push({
          label: 'Disponibles',
          value: live.available,
          chip: String(live.year),
          note:
            live === currentLive
              ? `de ${String(live.balance.entitledDays)} · hasta el ${formatDayWithYear(live.lastValidDay)}`
              : `vigentes hasta el ${formatDayWithYear(live.lastValidDay)}`,
          color: 'var(--color-brand)',
        });
      }
      /*
       * What the person has actually spent this period.
       *
       * `takenDays` alone reads as zero for everyone loaded with the roster: their
       * absences happened before the portal existed, so there is no request to
       * close and nothing ever increments that counter. `openingUsedDays` is
       * precisely that history — we know how many days they took even though we
       * do not know when — so the two together are the honest figure, and the one
       * that reconciles with the cell beside it.
       *
       * Not `entitledDays - availableDays`, which would look the same today and
       * be wrong tomorrow: that difference also absorbs the scheduled and pending
       * days already counted under «En revisión», and `availableDays` floors at
       * zero, so anyone overdrawn would read as having spent exactly their
       * entitlement.
       */
      items.push({
        label: 'Tomados',
        value: currentBalance.openingUsedDays + currentBalance.takenDays,
        chip: String(currentBalance.year),
        /* Lo descontado al abrir puede ser deuda del periodo anterior y no días de
           antes del portal: se dice lo que es. Ver `carriedDebtDays`. */
        note:
          currentBalance.carriedDebtDays > 0
            ? `incluye ${String(currentBalance.carriedDebtDays)} de deuda del periodo anterior`
            : currentBalance.openingUsedDays > 0
              ? `incluye ${String(currentBalance.openingUsedDays)} previos al portal`
              : 'aprobados y cerrados',
        color: 'var(--color-success)',
      });
      /*
       * A zero is not information here. Nothing waiting on a leader is the
       * ordinary state, and a cell that always reads zero costs a quarter of the
       * band and teaches people to stop reading it.
       */
      /* Una solicitud en revisión puede estar cargada al periodo anterior: se
         suman todos los vigentes. */
      const pendingDays = lives.reduce((sum, live) => sum + live.balance.pendingDays, 0);
      if (pendingDays > 0) {
        items.push({
          label: 'En revisión',
          value: pendingDays,
          chip: 'Pendiente',
          note: 'esperando al líder',
          color: 'var(--color-warning)',
        });
      }
    }

    // Same rule: nobody out today is the common case, not a number worth a cell.
    if (outCount.data !== undefined && outCount.data > 0) {
      items.push({
        label: 'Fuera hoy',
        value: outCount.data,
        chip: 'Hoy',
        note: `de ${String(activePeople.data ?? 0)} del área`,
        color: 'var(--color-success)',
      });
    }

    return items;
  }, [
    variant,
    canSeeOwn,
    lives,
    currentLive,
    currentBalance,
    outCount.data,
    activePeople.data,
    area.subAreaCount,
    area.pendingAccounts,
    area.activeHolidays,
    areaName,
    year,
    system,
  ]);

  const expiry = useMemo<ExpiryBanner | null>(() => {
    /*
     * Se deriva del periodo, no se lee del documento.
     *
     * `expiringDays` y `expiresOn` los escribe `ensureBalance`, que corre cuando
     * alguien envía o se decide algo — así que un saldo que nadie ha tocado desde
     * el cambio los trae en cero, y el aviso no saldría justo para quien lleva
     * meses sin pedir nada, que es exactamente a quien hay que avisarle. Aquí se
     * calculan de lo que sí es cierto siempre: el aniversario, «Vencimiento del
     * saldo» y los días que quedan. Los campos guardados siguen escribiéndose
     * porque la tarea semanal consulta por ellos.
     *
     * Sólo dentro de la ventana de aviso: sin ella, todo el que tenga un día sin
     * gozar vería la advertencia todo el año, y una advertencia permanente se
     * aprende a no ver. Ver `expiringLive`.
     */
    const live = expiringLive(lives, session.hireDate, today);
    if (live === null) return null;

    const expiringDays = live.available;
    const daysLeft = live.daysLeft;
    const unit = expiringDays === 1 ? 'día' : 'días';
    const verb = expiringDays === 1 ? 'vence' : 'vencen';

    /*
     * El periodo se nombra por sus años, no por dos cableados: el de cada quien
     * abre en su aniversario, así que un año fijo le diría a quien renueva en
     * octubre que sus días son de un periodo que no vivió.
     */
    const periodLabel = `${String(live.year)} – ${String(live.year + 1)}`;

    return {
      days: expiringDays,
      headline: `${String(expiringDays)} ${unit} del periodo ${periodLabel} ${verb} en ${String(
        daysLeft,
      )} ${daysLeft === 1 ? 'día' : 'días'}`,
      note: `Valen ${String(session.config.rules.expiryMonths)} meses desde el aniversario laboral en que los ganaste: el ${formatDayWithYear(
        live.lastValidDay,
      )} es el último día para gozarlos, y al pedir se gastan antes que los del periodo nuevo. No se pagan en efectivo.`,
    };
  }, [session.hireDate, session.config.rules.expiryMonths, lives, today]);

  const subAreaById = useMemo(
    () => new Map((subAreas.data ?? []).map((s) => [s.id, s])),
    [subAreas.data],
  );

  /**
   * Cuánta gente hay fuera esta semana, antes de recortar la lista.
   *
   * El subtítulo contaba `outThisWeek.length`, que es la lista **ya truncada a
   * cinco filas** para que quepa en el panel. Con seis personas fuera la frase
   * decía cinco, y el número no tenía forma de crecer nunca — un tope de
   * presentación gobernando una afirmación de hecho.
   */
  const outThisWeekTotal = (outNow.data ?? []).length;

  const outThisWeek = useMemo<OutRow[]>(
    () =>
      [...(outNow.data ?? [])]
        .sort((a, b) => a.range.start.epochDay - b.range.start.epochDay)
        .slice(0, OUT_LIMIT)
        .map((request) => ({
        id: request.id,
        name: request.personName,
        subAreaName: request.subAreaName,
        /*
         * Quién cubre, que es la pregunta que trae a un compañero a esta lista.
         *
         * Saber que alguien está fuera sirve para no buscarlo; saber a quién
         * buscar en su lugar es lo que desatasca el trabajo. El dato ya viajaba
         * en la solicitud —`replacementName`, desnormalizado— y no se enseñaba en
         * ningún sitio fuera de la ficha de la propia solicitud.
         *
         * Vacío cuando la solicitud no lleva respaldo: el módulo de reemplazo se
         * puede apagar por área, así que una ausencia sin nombre es un estado
         * legítimo y no un dato que falte.
         */
        replacementName: request.replacementName,
        rangeLabel: formatDateRange(request.range),
        color:
          request.subAreaId === null
            ? seedColorForSubArea(request.subAreaName)
            : subAreaColor(subAreaById.get(request.subAreaId)),
        })),
    [outNow.data, subAreaById],
  );

  const inbox = useMemo<QueueRow[]>(
    () => [
      ...(queue.data ?? []).map(toQueueRow),
      ...(pendingIncapacities.data ?? []).map((incapacity) => ({
        id: incapacity.id,
        kind: 'Incapacidad',
        board: 'incapacidades' as const,
        kindBackground: 'var(--color-warning-bg)',
        kindForeground: 'var(--color-warning-ink)',
        kindAccent: 'var(--color-warning)',
        folio: incapacity.folio,
        personName: incapacity.personName,
        meta: `${incapacity.folio} · ${formatDateRange(incapacity.range)} · ${incapacity.kind}${
          incapacity.observation === null ? '' : ` · ${incapacity.observation}`
        }`,
        status: incapacity.status,
        statusBackground: incapacityStatusColors(incapacity.status).background,
        statusForeground: incapacityStatusColors(incapacity.status).foreground,
        accentColor: seedColorForSubArea(incapacity.subAreaName),
        actionLabel: 'Validar',
      })),
      ...(pendingPermits.data ?? []).map((permit) => ({
        id: permit.id,
        kind: permit.kind,
        board: 'permisos' as const,
        kindBackground: 'var(--color-success-bg)',
        kindForeground: 'var(--color-success-ink)',
        kindAccent: 'var(--color-success)',
        folio: permit.folio,
        personName: permit.personName,
        meta: `${permit.folio} · ${formatDateRange(permit.range)} · ${
          permit.withPay ? 'Con goce' : 'Sin goce'
        }`,
        status: permit.status,
        statusBackground: permissionStatusColors(permit.status).background,
        statusForeground: permissionStatusColors(permit.status).foreground,
        accentColor: seedColorForSubArea(permit.subAreaName),
        actionLabel: 'Aprobar',
      })),
    ],
    [queue.data, pendingIncapacities.data, pendingPermits.data],
  );

  const ownSubAreaName =
    session.subAreaId === null
      ? areaName
      : (subAreaById.get(session.subAreaId)?.name ?? areaName);

  const firstName = session.displayName.split(/\s+/)[0] ?? session.displayName;
  const inReview = mine.filter(
    (r) => r.status === 'Por revisar' || r.status === 'En revisión' || r.status === 'Visto bueno',
  ).length;

  /**
   * The subtitle states what is actually waiting, rather than greeting and
   * saying nothing. It degrades to a plain welcome when there is no news.
   */
  const subtitle = useMemo(() => {
    if (variant === 'super') {
      const total = system.activeFeatureCount + system.offFeatureCount;
      return `${String(system.activeFeatureCount)} de ${String(
        total,
      )} features activas · ${String(system.areaCount)} ${
        system.areaCount === 1 ? 'área' : 'áreas'
      } y ${String(system.subAreaCount)} sub áreas configuradas.`;
    }

    if (variant === 'admin') {
      return `Área ${areaName} · ${String(area.activePeople)} ${
        area.activePeople === 1 ? 'persona' : 'personas'
      }, ${String(area.pendingAccounts)} ${
        area.pendingAccounts === 1 ? 'cuenta por validar' : 'cuentas por validar'
      }.`;
    }

    const parts: string[] = [];
    if (canApprove && inbox.length > 0) {
      parts.push(
        `${String(inbox.length)} ${
          inbox.length === 1 ? 'solicitud espera tu decisión' : 'solicitudes esperan tu decisión'
        }`,
      );
    } else if (inReview > 0) {
      parts.push(
        `${String(inReview)} ${inReview === 1 ? 'solicitud en revisión' : 'solicitudes en revisión'}`,
      );
    }
    if (outThisWeekTotal > 0) {
      parts.push(
        `${String(outThisWeekTotal)} ${
          outThisWeekTotal === 1 ? 'compañero fuera' : 'compañeros fuera'
        } esta semana`,
      );
    }
    if (parts.length === 0) return 'No tienes nada pendiente por ahora.';
    return canApprove ? `${parts.join(' · ')}.` : `Tienes ${parts.join(' y ')}.`;
  }, [
    variant,
    canApprove,
    inbox.length,
    inReview,
    outThisWeekTotal,
    areaName,
    area.activePeople,
    area.pendingAccounts,
    system,
  ]);

  /**
   * The suggestion card.
   *
   * Only shown when it can say something specific — a generic tip in a coloured
   * panel is decoration, and it trains people to skip the panel.
   */
  const tip = useMemo<string | null>(() => {
    if (variant === 'super') return system.tip;

    /* Sólo lo que todavía se puede esquivar, y en el tiempo verbal que le toca.
       El porqué de cada borde vive en `absenceTip.ts`, con sus pruebas. */
    const chosen = chooseAbsenceTip(outNow.data ?? [], today);
    if (chosen === null) return null;

    const { absence, inProgress } = chosen;
    return `${absence.personName} ${inProgress ? 'está' : 'estará'} fuera en ${
      absence.subAreaName
    } (${formatDateRange(absence.range)}). Si tus fechas caen ahí, considera moverlas para no dejar la sub área corta.`;
  }, [variant, system.tip, outNow.data, today]);

  const greeting =
    variant === 'super'
      ? 'Estado del sistema'
      : variant === 'admin'
        ? 'Panel de RR.HH.'
        : variant === 'leader'
          ? `Bandeja de ${firstName} · líder de ${ownSubAreaName}`
          : `Hola, ${firstName} 👋`;

  const statsTitle =
    variant === 'super'
      ? 'Estado del sistema'
      : variant === 'admin'
        ? 'El área de un vistazo'
        : variant === 'leader'
          ? 'Tu equipo de un vistazo'
          : 'Tu periodo de un vistazo';

  return {
    variant,
    greeting,
    subtitle,
    statsTitle,
    statsScope:
      variant === 'super'
        ? `Todas las áreas · ${String(year)}`
        : /* El periodo de quien mira, no el año en curso: quien renueva en
             septiembre está en el suyo del año anterior, y el rótulo tiene que
             decir el mismo que la tarjeta de saldo de la barra lateral. */
          `${areaName} · periodo ${String(
            currentLive?.year ?? periodYearFor(session.hireDate, today),
          )}`,
    stats,
    expiry,
    myRequests: mine.map(toRecent),
    pendingApprovals: inbox,
    outThisWeek,
    tip,
    loading: myRequests.isLoading || queue.isLoading || balance.isLoading,
    error: firstError([myRequests.error, queue.error, outCount.error]),
    onNewRequest: go('/ausencias'),
    onScheduleExpiring: go('/ausencias'),
    onOpenRequests: go('/ausencias'),
    onOpenCalendar: go('/calendario'),
    // The board carries the chip in the URL, so a permit row opens on permits.
    onOpenBoard: (board?: QueueRow['board']) => {
      void navigate(board === undefined ? '/aprobaciones' : `/aprobaciones?tipo=${board}`);
    },
    onOpenAdmin: go('/administrador'),
    onOpenConfig: go('/configuracion'),
    onOpenLog: go('/bitacora'),
    area,
    system,
  };
};

const toRecent = (request: VacationRequest): RecentRequest => {
  const colors = statusChipColors(request.status);
  return {
    id: request.id,
    month: MONTHS_SHORT[request.range.start.month - 1] ?? '',
    day: request.range.start.day,
    rangeLabel: formatDateRange(request.range),
    meta: `${String(request.businessDays)} días hábiles${
      request.replacementName.length > 0 ? ` · reemplazo ${request.replacementName}` : ''
    } · ${request.folio}`,
    status: request.status,
    statusBackground: colors.background,
    statusForeground: colors.foreground,
    statusAccent: statusAccentColor(request.status),
    steps: lifecycleSteps(request.status, request.requiredSignatures),
  };
};

const toQueueRow = (request: VacationRequest): QueueRow => {
  const colors = statusChipColors(request.status);
  return {
    accentColor: seedColorForSubArea(request.subAreaName),
    id: request.id,
    kind: 'Vacaciones',
    board: 'vacaciones',
    kindBackground: 'var(--color-brand-bg)',
    kindForeground: 'var(--color-brand-ink)',
    kindAccent: 'var(--color-brand)',
    folio: request.folio,
    personName: request.personName,
    meta: `${request.folio} · ${formatDateRange(request.range)} · ${String(
      request.businessDays,
    )} días${request.replacementName.length > 0 ? ` · reemplazo ${request.replacementName}` : ''}${
      request.requiredSignatures === 2 ? ' · requiere doble visto bueno' : ''
    }`,
    status: request.status,
    statusBackground: colors.background,
    statusForeground: colors.foreground,
    // A second signature is not an approval, and calling it one would let a
    // leader think they had closed something they only advanced.
    actionLabel: request.status === 'Visto bueno' ? 'Dar visto bueno' : 'Aprobar',
  };
};

/**
 * The sentence the user reads — and, on the way there, the line that says why.
 *
 * `unavailable` is the port's catch-all: a dropped connection, yes, but also the
 * `failed-precondition` of a missing composite index. Both reach the screen as
 * «revisa tu red», which sends whoever debugs it to the network and away from the
 * index. The `detail` the adapter built is the only thing that tells them apart,
 * and it was being dropped here without ever being read, so it goes to the log
 * before the copy loses it.
 */
const describe = (error: { kind: string; detail?: string; what?: string }): string => {
  getServices().logger.error('sección de Inicio no cargó', { error });
  switch (error.kind) {
    case 'permission-denied':
      return 'No tienes acceso a esa información.';
    case 'unavailable':
      return `No pudimos conectar. Revisa tu red e intenta de nuevo.${technical(error.detail)}`;
    default:
      return `No pudimos cargar esta sección.${technical(error.detail)}`;
  }
};

/**
 * The technical reason, carried in the banner itself.
 *
 * The console is not a reachable place. These failures happen on employees' own
 * machines, and «mándame la consola del navegador» is not something you can ask
 * thirty-seven people — the first one of these took two rounds of guessing
 * precisely because nobody who could read the error was the person seeing it. The
 * `detail` is the only string that tells a missing index from a lapsed session,
 * so it rides along with the sentence, where a screenshot captures it.
 *
 * Truncated because a `failed-precondition` from Firestore carries a full console
 * URL and would push the readable part off the banner.
 */
const DETAIL_LIMIT = 140;

const technical = (detail?: string): string => {
  if (detail === undefined || detail.length === 0) return '';
  const trimmed = detail.length > DETAIL_LIMIT ? `${detail.slice(0, DETAIL_LIMIT)}…` : detail;
  return ` · ${trimmed}`;
};

const firstError = (errors: readonly (Error | null)[]): string | null => {
  const found = errors.find((error): error is Error => error instanceof Error);
  return found === undefined ? null : found.message;
};

export { statusChipColors };
